// 小さなDOMヘルパー。文字列テンプレートで組み、ユーザー由来の文字は必ず esc() を通す。
import { OMAMORI } from '@hyakki/core';
import type { Grant, ResourceKind, Resources } from '../api';

export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector<T>(sel);
export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => [...root.querySelectorAll<T>(sel)];

export function esc(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

export const fmt = (n: number | undefined | null) => (n == null ? '—' : Math.floor(n).toLocaleString('ja-JP'));

export const RES_LABEL: Record<ResourceKind, string> = {
  zeni: '銭',
  reidama: '霊玉',
  zaimoku: '材木',
  hyoro: '兵糧',
  exp: 'EXP',
  fuda: '式神の札',
  kuji: 'ガチャ券',
  soutou: '掃討チケット',
};
export const RES_ICON: Record<ResourceKind, string> = {
  zeni: 'zeni',
  reidama: 'reidama',
  zaimoku: 'zaimoku',
  hyoro: 'hyoro',
  exp: 'exp',
  fuda: 'fuda',
  kuji: 'kuji',
  soutou: 'ticket',
};

/** 資源アイコン（UIアイコン素材ができるまでは CSS の図形）。 */
export const resIcon = (k: ResourceKind) => `<i class="ri ri-${RES_ICON[k]}" aria-hidden="true"></i>`;

const ITEM_LABEL: Record<string, string> = { rename_ticket: '名前変更券' };
/** 道具IDの表示名（rename_ticket / omamori:<id>） */
export function itemLabel(id: string): string {
  if (ITEM_LABEL[id]) return ITEM_LABEL[id];
  if (id.startsWith('omamori:')) return `お守り：${OMAMORI[id.slice(8)]?.name ?? id.slice(8)}`;
  return id;
}

export function grantText(g: Grant): string {
  if (g.kind === 'item') return `${esc(itemLabel(g.id))} ×${fmt(g.amount)}`;
  return `${RES_LABEL[g.kind]} ${fmt(g.amount)}`;
}
export function grantsHtml(gs: Grant[] | undefined): string {
  if (!gs?.length) return '<span class="muted">なし</span>';
  return `<ul class="grants">${gs.map((g) => `<li>${g.kind === 'item' ? '<i class="ri ri-item"></i>' : resIcon(g.kind)}<span>${grantText(g)}</span></li>`).join('')}</ul>`;
}
export function costHtml(r: Resources | null | undefined): string {
  if (!r) return '';
  return Object.entries(r)
    .map(([k, v]) => `<span class="cost">${resIcon(k as ResourceKind)}${fmt(v)}</span>`)
    .join('');
}

export function timeAgo(at: number) {
  const d = Date.now() - at;
  if (d < 60_000) return 'たった今';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}分前`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}時間前`;
  return new Date(at).toLocaleDateString('ja-JP');
}
export const hhmm = (at: number) => new Date(at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
/** チャットの時刻：今日なら「10:41」、前日より前は「10/29 10:41」、去年より前は「2025/10/29 10:41」 */
export function chatTime(at: number) {
  const d = new Date(at);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return hhmm(at);
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  return `${d.getFullYear() === now.getFullYear() ? md : `${d.getFullYear()}/${md}`} ${hhmm(at)}`;
}
export function until(at: number | null | undefined) {
  if (!at) return '';
  const d = at - Date.now();
  if (d <= 0) return 'まもなく';
  const h = Math.floor(d / 3_600_000);
  if (h >= 48) return `あと${Math.floor(h / 24)}日`;
  if (h >= 1) return `あと${h}時間`;
  return `あと${Math.max(1, Math.floor(d / 60_000))}分`;
}

export const starsHtml = (s: readonly boolean[], label = true) =>
  `<span class="stars" ${label ? `aria-label="星${s.filter(Boolean).length}つ"` : 'aria-hidden="true"'}>${s.map((x) => `<i class="star ${x ? 'on' : ''}">★</i>`).join('')}</span>`;

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** data-act を持つ要素のクリックをまとめて受ける。 */
export function onAct(root: HTMLElement, handlers: Record<string, (el: HTMLElement, ev: Event) => void>) {
  const fn = (ev: Event) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!el || !root.contains(el) || (el as HTMLButtonElement).disabled) return;
    const h = handlers[el.dataset.act!];
    if (h) {
      ev.preventDefault();
      h(el, ev);
    }
  };
  root.addEventListener('click', fn);
  return () => root.removeEventListener('click', fn);
}

/** 報酬の表示：Grant[]（サーバーの台帳）でも Bundle（meta の {zeni:…}）でも受け付ける */
export function rewardHtml(v: unknown): string {
  if (Array.isArray(v)) return grantsHtml(v as Grant[]);
  if (v && typeof v === 'object') {
    const gs: Grant[] = [];
    for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
      if (typeof n === 'number' && n > 0 && k in RES_LABEL) gs.push({ kind: k as ResourceKind, amount: n });
    }
    return grantsHtml(gs);
  }
  return grantsHtml([]);
}

/** 日本時間の日付キー（万屋・任務の日替わり。サーバーの jstDay と同じ形式 YYYY-MM-DD） */
export const jstDay = (now = Date.now()) => new Date(now + 9 * 3_600_000).toISOString().slice(0, 10);

/** 長い名前は折り返す位置を決める（CSS の word-break: keep-all と組み合わせ。「おまもりガチ／ャ」にしない） */
export const wrapLabel = (label: string) => label.replace('おまもりガチャ', 'おまもり<wbr>ガチャ');
